import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Close,
  CloseOutlined,
  History,
  SearchOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";

import isNil from "lodash/isNil";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import BasicModal from "../../../../components/modals/BasicModal";
import Flx from "../../../../components/layout/Flx";
import TextInput from "../../../../components/inputs/TextInput";
import Txt from "../../../../components/typography/Txt";
import { LOAN_DRILLDOWN_INDEX } from "./loanDrilldownIndex";

const RECENT_DRILLDOWN_SEARCHES_KEY = "loanDrilldownGlobalLoanRecentSearches";
const MAX_RECENT_SEARCHES = 7;
const MAX_RESULTS = 25;

/**
 * Flatten the nested drilldown index into searchable items.
 * Index shape:
 * { label, path, children?, includes? }
 */
const flattenIndex = (nodes, parentChain = []) => {
  const results = [];

  nodes.forEach((node) => {
    const depth = parentChain.length;

    let nodeIncludes = Array.isArray(node.includes) ? [...node.includes] : [];

    // for field-level nodes, force label into includes
    if (depth >= 2) {
      const hasLabelInIncludes = nodeIncludes.some(
        (inc) => String(inc).toLowerCase() === String(node.label).toLowerCase(),
      );
      if (!hasLabelInIncludes) nodeIncludes.push(node.label);
    }

    const nextChain = [
      ...parentChain,
      {
        label: node.label,
        path: node.path,
        includes: nodeIncludes,
        isRoute: !!node.isRoute, // << IMPORTANT
      },
    ];

    const hasChildren =
      Array.isArray(node.children) && node.children.length > 0;

    const id = nextChain
      .map((c) => c.path)
      .filter(Boolean)
      .join("__");

    results.push({
      id,
      label: node.label,
      path: node.path,
      includes: nodeIncludes,
      isRoute: !!node.isRoute, // << IMPORTANT
      chain: nextChain,
      isLeaf: !hasChildren,
    });

    if (hasChildren) {
      results.push(...flattenIndex(node.children, nextChain));
    }
  });

  return results;
};

/**
 * Build a URL for a chain.
 * Example chain paths: fees-and-closing -> wire-instructions
 * => /loan/:loanNumber/fees-and-closing/wire-instructions
 */
const getNearestRoutableChain = (chain) => {
  if (!chain?.length) return null;

  let lastRouteIdx = -1;
  chain.forEach((c, i) => {
    if (c.isRoute) lastRouteIdx = i;
  });

  // If a routable ancestor exists, return chain up to it
  if (lastRouteIdx >= 0) return chain.slice(0, lastRouteIdx + 1);

  // Otherwise fallback to top-level
  return [chain[0]];
};

const chainToPath = (loanNumber, chain) => {
  if (!loanNumber || !chain?.length) return null;

  const routableChain = getNearestRoutableChain(chain);
  if (!routableChain?.length) return null;

  const segments = routableChain
    .map((c) => c.path)
    .filter((s) => !isNil(s) && String(s).trim().length > 0);

  return `/loan/${loanNumber}/${segments.join("/")}`;
};

const LoanGlobalSearch = memo(({ sx }) => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanNumber = loanDrilldown?.loanNumber;

  const [recentSearches, setRecentSearches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const listRef = useRef(null);

  const flatIndex = useMemo(() => flattenIndex(LOAN_DRILLDOWN_INDEX), []);

  const filteredResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];

    return flatIndex
      .map((item) => {
        const chainLabels = (item.chain || []).map((c) => c.label);
        const chainIncludes = (item.chain || []).flatMap(
          (c) => c.includes || [],
        );

        const rawHaystacks = [
          item.label,
          item.path,
          ...(item.includes || []),
          ...chainLabels,
          ...chainIncludes,
        ].filter(Boolean);

        const haystacks = rawHaystacks.map((x) => String(x).toLowerCase());

        const matchIndex = haystacks.findIndex((h) => h.includes(term));
        if (matchIndex === -1) return null;

        // store which original token matched (could be include, label, chain label, etc.)
        const matchedToken = rawHaystacks[matchIndex];

        return {
          ...item,
          matchedToken,
        };
      })
      .filter(Boolean)
      .slice(0, MAX_RESULTS);
  }, [flatIndex, searchTerm]);

  const showSearchResults = searchTerm.trim().length > 0;

  useEffect(() => {
    if (!showSearchResults) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredResults.length > 0 ? 0 : -1);
  }, [showSearchResults, filteredResults.length]);

  useEffect(() => {
    const loadRecent = () => {
      try {
        const saved = localStorage.getItem(RECENT_DRILLDOWN_SEARCHES_KEY);
        if (saved) {
          setRecentSearches(JSON.parse(saved));
        }
      } catch (e) {
        console.error("Error loading drilldown recent searches:", e);
      }
    };

    if (show) {
      loadRecent();
      setSearchTerm("");
      setHighlightedIndex(-1);
    }
  }, [show]);

  const saveRecentSearch = useCallback((item) => {
    try {
      const searchItem = {
        id: item.id,
        label: item.label,
        chain: item.chain,
        path: item.path,
        timestamp: Date.now(),
      };

      const saved = localStorage.getItem(RECENT_DRILLDOWN_SEARCHES_KEY);
      let searches = saved ? JSON.parse(saved) : [];

      searches = searches.filter((s) => s.id !== item.id);
      searches.unshift(searchItem);
      searches = searches.slice(0, MAX_RECENT_SEARCHES);

      localStorage.setItem(
        RECENT_DRILLDOWN_SEARCHES_KEY,
        JSON.stringify(searches),
      );
      setRecentSearches(searches);
    } catch (e) {
      console.error("Error saving drilldown recent search:", e);
    }
  }, []);

  const deleteRecentSearch = useCallback((id, e) => {
    e.stopPropagation();
    try {
      const saved = localStorage.getItem(RECENT_DRILLDOWN_SEARCHES_KEY);
      let searches = saved ? JSON.parse(saved) : [];

      searches = searches.filter((s) => s.id !== id);

      localStorage.setItem(
        RECENT_DRILLDOWN_SEARCHES_KEY,
        JSON.stringify(searches),
      );
      setRecentSearches(searches);
    } catch (e) {
      console.error("Error deleting drilldown recent search:", e);
    }
  }, []);

  const clearAllRecentSearches = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_DRILLDOWN_SEARCHES_KEY);
      setRecentSearches([]);
    } catch (e) {
      console.error("Error clearing drilldown recent searches:", e);
    }
  }, []);

  const handleOnClose = useCallback(() => {
    setShow(false);
  }, []);

  const handleOnClick = useCallback(
    (item) => {
      if (!item?.computedPath) return;

      console.log("item.computedPathitem.computedPath", item.computedPath);
      saveRecentSearch(item);
      navigate(item.computedPath);
      handleOnClose();
    },
    [navigate, handleOnClose, saveRecentSearch],
  );

  const scrollItemIntoView = (index) => {
    const listEl = listRef.current;
    if (!listEl) return;
    const child = listEl.querySelector(`[data-result-index="${index}"]`);
    if (child?.scrollIntoView) {
      child.scrollIntoView({ block: "nearest" });
    }
  };

  const handleInputKeyDown = useCallback(
    (e) => {
      if (!showSearchResults) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = Math.min(prev + 1, filteredResults.length - 1);
          scrollItemIntoView(next);
          return next;
        });
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = Math.max(prev - 1, 0);
          scrollItemIntoView(next);
          return next;
        });
      }

      if (e.key === "Enter") {
        if (highlightedIndex >= 0 && filteredResults[highlightedIndex]) {
          e.preventDefault();
          // IMPORTANT: use resultsWithPaths so we have full computed URL
          const item = resultsWithPaths[highlightedIndex];
          if (item?.path) handleOnClick(item);
        }
      }

      if (e.key === "Escape") {
        e.preventDefault();
        handleOnClose();
      }
    },
    [
      showSearchResults,
      filteredResults,
      highlightedIndex,
      handleOnClick,
      handleOnClose,
      // resultsWithPaths is defined below; safe because it’s stable from useMemo
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
  );

  // Prepare items with computed paths from redux loan number
  const resultsWithPaths = useMemo(() => {
    return filteredResults.map((r) => {
      const computedPath = chainToPath(loanNumber, r.chain);
      return {
        ...r,
        computedPath,
      };
    });
  }, [filteredResults, loanNumber]);

  const recentsWithPaths = useMemo(() => {
    return recentSearches.map((r) => ({
      ...r,
      computedPath: chainToPath(loanNumber, r.chain),
    }));
  }, [recentSearches, loanNumber]);

  const styles = useMemo(() => {
    return {
      color: grey[500],
      fontWeight: 400,
      border: `1px solid ${grey[300]}`,
      pl: 2,
      pr: 9,
      borderRadius: 50,
      textTransform: "none",
      ...sx,
    };
  }, [sx]);
  return (
    <>
      <Button
        variant="text"
        startIcon={<SearchOutlined className="thin" />}
        color="inherit"
        sx={styles}
        onClick={() => setShow(true)}
      >
        Search in loan for...
      </Button>

      <BasicModal
        show={show}
        onClose={handleOnClose}
        title="Loan Drilldown Search"
        headerContent={
          <Flx sx={{ p: 2, borderBottom: "1px solid #f2f2f2" }} flexGrow={1} ac>
            <SearchOutlined className="thin" />
            <TextInput
              autoFocus
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e)}
              onKeyDown={handleInputKeyDown}
              sx={{
                flexGrow: 1,
                fieldset: { border: "none" },
              }}
              placeholder="Search within this loan... (e.g., Wire Instructions, Interest Rate)"
              inputProps={{
                role: "combobox",
                "aria-expanded": showSearchResults,
                "aria-controls": "loan-drilldown-search-results",
                "aria-activedescendant":
                  highlightedIndex >= 0
                    ? `drilldown-search-result-${highlightedIndex}`
                    : undefined,
              }}
            />
            <IconButton onClick={handleOnClose}>
              <CloseOutlined className="thin" />
            </IconButton>
          </Flx>
        }
        headerSx={{ pb: "8px !important" }}
        bodySx={{ p: 0 }}
        maxWidth="sm"
      >
        <>
          {/* Search Results */}
          {showSearchResults && (
            <Box sx={{ p: 2 }}>
              <Flx jb ac sx={{ mb: 1 }}>
                <Txt sx={{ fontWeight: 500, fontSize: "11px" }}>
                  Search Results
                </Txt>
                <Txt variant="caption" sx={{ color: grey[500] }}>
                  {resultsWithPaths.length} result
                  {resultsWithPaths.length === 1 ? "" : "s"}
                </Txt>
              </Flx>

              {resultsWithPaths.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 2,
                    color: grey[500],
                    borderTop: `1px solid ${grey[200]}`,
                  }}
                >
                  <Typography variant="body2">
                    No drilldown areas match your search
                  </Typography>
                </Box>
              ) : (
                <List
                  id="loan-drilldown-search-results"
                  ref={listRef}
                  role="listbox"
                  sx={{
                    py: 0,
                    maxHeight: 480,
                    overflowY: "auto",
                    border: `1px solid ${grey[100]}`,
                    borderRadius: 2,
                  }}
                >
                  {resultsWithPaths.map((opt, idx) => {
                    const isActive = idx === highlightedIndex;
                    const chainLabels = (opt.chain || []).map((c) => c.label);
                    const baseBreadcrumb = chainLabels.slice(0, -1).join(" > ");

                    // last segment should be the matched include/token if present,
                    // otherwise fallback to the actual leaf label
                    const lastSegment =
                      opt.matchedToken || chainLabels[chainLabels.length - 1];

                    const breadcrumb = baseBreadcrumb
                      ? `${baseBreadcrumb} > ${lastSegment}`
                      : `${lastSegment}`;
                    const isNavigable = !!opt.computedPath;

                    return (
                      <ListItem
                        key={`${opt.id}-${idx}`}
                        disablePadding
                        data-result-index={idx}
                        id={`drilldown-search-result-${idx}`}
                        role="option"
                        aria-selected={isActive}
                      >
                        <ListItemButton
                          selected={isActive}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          onClick={() => isNavigable && handleOnClick(opt)}
                          sx={{
                            py: 1,
                            px: 1,
                            borderRadius: 1,
                            borderBottom: `1px solid ${grey[100]}`,
                            "&.Mui-selected": { backgroundColor: grey[100] },
                            "&.Mui-selected:hover": {
                              backgroundColor: grey[100],
                            },
                            "&:hover": { backgroundColor: grey[50] },
                            opacity: isNavigable ? 1 : 0.6,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Flx column gap={0.4}>
                                <Txt sx={{ fontSize: "12px" }}>{opt.label}</Txt>
                                <Txt
                                  sx={{
                                    fontSize: "10px",
                                    color: grey[600],
                                  }}
                                >
                                  {breadcrumb}
                                </Txt>
                              </Flx>
                            }
                            primaryTypographyProps={{
                              variant: "body2",
                              sx: { fontWeight: 400 },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          )}

          {/* Recent Searches */}
          {!showSearchResults && recentsWithPaths.length > 0 && (
            <Box sx={{ p: 2 }}>
              <Flx jb ac sx={{ mb: 1 }}>
                <Flx ac gap={0.4}>
                  <History sx={{ fontSize: "15px", color: grey[600] }} />
                  <Txt sx={{ fontWeight: 500, fontSize: "11px" }}>
                    Recent Searches
                  </Txt>
                </Flx>
                <Txt
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={clearAllRecentSearches}
                >
                  Clear All
                </Txt>
              </Flx>

              <List
                sx={{
                  py: 0,
                  border: `1px solid ${grey[100]}`,
                  borderRadius: 2,
                }}
              >
                {recentsWithPaths.map((search) => {
                  const breadcrumb = (search.chain || [])
                    .map((c) => c.label)
                    .join(" > ");

                  return (
                    <ListItem
                      key={search.id}
                      disablePadding
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => deleteRecentSearch(search.id, e)}
                          sx={{
                            opacity: 0.6,
                            "&:hover": { opacity: 1 },
                          }}
                        >
                          <Close sx={{ fontSize: 15 }} />
                        </IconButton>
                      }
                    >
                      <ListItemButton
                        onClick={() => search.path && handleOnClick(search)}
                        sx={{
                          py: 1,
                          px: 1,
                          borderRadius: 1,
                          borderBottom: `1px solid ${grey[100]}`,
                          "&:hover": { backgroundColor: grey[50] },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Flx column gap={0.4}>
                              <Txt sx={{ fontSize: "12px" }}>
                                {search.label}
                              </Txt>
                              <Txt sx={{ fontSize: "10px", color: grey[600] }}>
                                {breadcrumb}
                              </Txt>
                            </Flx>
                          }
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { fontWeight: 400 },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {/* Empty Recent State */}
          {!showSearchResults && recentsWithPaths.length === 0 && (
            <Box
              sx={{
                textAlign: "center",
                py: 5,
                color: grey[500],
                borderTop: `1px solid ${grey[200]}`,
              }}
            >
              <History sx={{ fontSize: 24, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">No recent searches</Typography>
            </Box>
          )}
        </>
      </BasicModal>
    </>
  );
});

export default LoanGlobalSearch;
